import { DT } from "../../constants.ts";
import { add, scale, sub } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, setHv } from "../../sim/components.ts";
import { addOverhealth, applyDamage, applyStatus, explode, isEnemy, lockOn, playersInRadius, spawnZone } from "../../sim/combat.ts";
import { beginTargeting, endTargeting, targetConfirmed, targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, zk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, flight, launchProjectile, ownerAlive, ready, secs, simpleProjectile, toGround, trigger, useUlt } from "../kitlib.ts";

export const V = { beamTarget: 0, chaos: 1, phase: 2, ultPulse: 3, recharge: 4 } as const;
export const PK = { Burst: pk(HeroId.ScarletTwitch, 0), Seal: pk(HeroId.ScarletTwitch, 1) } as const;
export const ZK = { Seal: zk(HeroId.ScarletTwitch, 0) } as const;
export const FX = { Chaos: fxk(HeroId.ScarletTwitch, 0), Burst: fxk(HeroId.ScarletTwitch, 1), Seal: fxk(HeroId.ScarletTwitch, 2), Erase: fxk(HeroId.ScarletTwitch, 3) } as const;

const def: HeroDef = {
  id: HeroId.ScarletTwitch,
  slug: "scarlet-twitch",
  name: "Scarlet Twitch",
  role: Role.Damage,
  color: "#d6246e",
  blurb: "Close-range chaos duelist with a lock-on beam, phasing flight, stun seal, and a channeled wipe ultimate.",
  health: 250,
  armor: 0,
  shields: 0,
  radius: 0.38,
  height: 1.8,
  headRadius: 0.23,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 3400,
  abilities: {
    [Slot.Primary]: { name: "Jitter Hex", key: "LMB", description: "Lock a short chaos beam to the nearest enemy and refill glitch charges.", cooldown: 0 },
    [Slot.Secondary]: { name: "Packet Burst", key: "RMB", description: "Spend chaos to fire an explosive magic packet.", cooldown: 0 },
    [Slot.Ability1]: { name: "Buffer Slip", key: "Shift", description: "Phase into free flight for 1.5 seconds. Stores 2 charges.", cooldown: 12, charges: 2 },
    [Slot.Ability2]: { name: "Lag Seal", key: "E", description: "Fire a seal that creates a field periodically stunning enemies.", cooldown: 12 },
    [Slot.Ultimate]: { name: "Hard Reboot", key: "Q", description: "Fly while channeling, then LMB to release early or auto-release after 4 seconds, erasing nearby enemies.", cooldown: 0 },
  },
  passive: { name: "Hover Error", description: "Hold Space while airborne to drift down slowly." },
};

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.chaos, 4);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      commonInput(sim, eid, input, pressed);
      if (hv(c, eid, V.chaos) < 4) {
        const r = hv(c, eid, V.recharge) + DT;
        if (r >= 5) {
          setHv(c, eid, V.chaos, Math.min(4, hv(c, eid, V.chaos) + 1));
          setHv(c, eid, V.recharge, 0);
        } else setHv(c, eid, V.recharge, r);
      }
      setHv(c, eid, V.phase, Math.max(0, c.Status.phased[eid]! / secs(2)));
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(4);
        addOverhealth(sim, eid, 100, secs(4), eid);
        beginTargeting(sim, eid, Slot.Ultimate);
        action(sim, eid, ActionKind.Ult, secs(4));
      }
      if (c.Ult.active[eid]! > 0) {
        setHv(c, eid, V.ultPulse, c.Ult.active[eid]! / secs(4));
        const release = targetConfirmed(sim, eid, Slot.Ultimate) || c.Ult.active[eid]! <= 1;
        const target = targetPoint(sim, eid, Slot.Ultimate).pos;
        const center = add(target, { x: 0, y: 1, z: 0 });
        for (const { eid: t } of playersInRadius(sim, center, 15, (e) => isEnemy(sim, eid, e), false)) {
          const p = sim.posOf(t);
          c.Ext.x[t] += (center.x - p.x) * 0.015;
          c.Ext.z[t] += (center.z - p.z) * 0.015;
        }
        if (release) {
          explode(sim, eid, center, { radius: 15, damage: 9999, minDamage: 9999, fxKind: FX.Erase });
          c.Ult.active[eid] = 0;
          setHv(c, eid, V.ultPulse, 0);
          endTargeting(sim, eid);
        }
        return;
      }

      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        applyStatus(sim, eid, "phased", 1.5, { source: eid });
        applyStatus(sim, eid, "invuln", 1.5, { source: eid });
        action(sim, eid, ActionKind.Cast, 15);
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 18);
        launchProjectile(sim, eid, PK.Seal, 60, input, { radius: 0.16, seconds: 1.4 });
      }
      if ((input.buttons & Btn.Secondary) && hv(c, eid, V.chaos) >= 1 && trigger(sim, eid, true, true)) {
        c.Weapon.fireCd[eid] = 30;
        setHv(c, eid, V.chaos, hv(c, eid, V.chaos) - 1);
        setHv(c, eid, V.recharge, 0);
        action(sim, eid, ActionKind.AltFire, 10);
        launchProjectile(sim, eid, PK.Burst, 120, input, { radius: 0.18, seconds: 1.6 });
      }
      if (input.buttons & Btn.Primary) {
        if (c.Weapon.fireCd[eid]! <= 0) {
          c.Weapon.fireCd[eid] = 6;
          action(sim, eid, ActionKind.Fire, 8);
        }
        if (sim.authority) {
          const target = lockOn(sim, eid, 20, 0.65, false);
          setHv(c, eid, V.beamTarget, target === undefined ? 0 : c.Net.id[target]!);
          if (target === undefined) return;
          const dealt = applyDamage(sim, target, 85 * DT, eid, { pos: add(sim.posOf(target), { x: 0, y: 1, z: 0 }), info: { kind: "aura", sourceKind: FX.Chaos } });
          if (dealt > 0) {
            setHv(c, eid, V.chaos, Math.min(4, hv(c, eid, V.chaos) + 0.15));
            setHv(c, eid, V.recharge, 0);
          }
        }
      } else if (sim.authority) setHv(c, eid, V.beamTarget, 0);
    },
    movement(sim, eid, input, mods) {
      if (sim.c.Status.phased[eid]! > 0 || sim.c.Ult.active[eid]! > 0) {
        flight(sim, eid, input, mods, { speed: sim.c.Ult.active[eid]! > 0 ? 4.5 : 9, vertical: 6, pitchClimb: true });
      } else if (!sim.c.Player.grounded[eid] && input.buttons & Btn.Jump && sim.c.Vel.y[eid]! < -3.5) {
        sim.c.Vel.y[eid] = -3.5;
        mods.gravityMul = 0.25;
      }
    },
  },
  projectiles: {
    [PK.Burst]: simpleProjectile(
      (sim, _p, owner, hit) => ownerAlive(sim, owner) && explode(sim, owner, hit.pos, { radius: 3, damage: 80, minDamage: 40, fxKind: FX.Burst }),
      { color: "#ff55b7", size: 0.18, trail: true },
    ),
    [PK.Seal]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        spawnZone(sim, owner, ZK.Seal, toGround(sim, hit.pos), 3, 4.5);
        sim.emit({ type: "explosion", pos: hit.pos, radius: 3, kind: FX.Seal });
      },
      { color: "#ff88c2", size: 0.16, trail: true },
    ),
  },
  zones: {
    [ZK.Seal]: {
      visual: { color: "#ff66cc", dome: true },
      tick(sim, z) {
        if (sim.c.Zone.age[z]! % 75 !== 1) return;
        const owner = sim.c.Zone.owner[z]!;
        if (!ownerAlive(sim, owner)) return;
        for (const { eid: t } of playersInRadius(sim, sim.posOf(z), sim.c.Zone.radius[z]!, (e) => isEnemy(sim, owner, e), false)) applyStatus(sim, t, "stun", 0.5, { source: owner });
      },
    },
  },
  fx: {
    [FX.Chaos]: { color: "#ff55b7" },
    [FX.Burst]: { color: "#ff2f95" },
    [FX.Seal]: { color: "#ff88c2" },
    [FX.Erase]: { color: "#ff007f" },
  },
  targets: {
    [Slot.Ultimate]: { start: "kit", self: true, range: 0, radius: 15, timeout: 4, verb: "Release" },
  },
};

void scale;
void sub;
export default module;
